import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useSession } from '../context/SessionContext';
import '../styles/service-page.css';
import '../styles/architecture-design.css';

const MIN_NAME_WORDS = 1;
const MIN_IDEA_WORDS = 10;
const DRAFT_KEY = 'dl_start_draft';

const wordCount = (s) => (s || '').trim().split(/\s+/).filter(Boolean).length;

const ArchitectureDesign = ({ user }) => {
  const navigate = useNavigate();
  const { createSession } = useSession();
  const [isStarting, setIsStarting] = useState(false);
  const [projectName, setProjectName] = useState('ProphetPlay.ai');
  const [initialIdea, setInitialIdea] = useState('ProphetPlay.ai is a high-volume, real-time prediction platform where users forecast outcomes of sports, entertainment, financial, and world events—without wagering real money. Players earn points, badges, and leaderboard rankings by making accurate predictions, with events ranging from NFL games to tech product launches. The goal is to combine the excitement of betting with the safety and inclusivity of a gamified, non-monetary experience. Phase 1 will focus on delivering a fast, engaging MVP with daily active use, event integrity, and scalable architecture to support thousands of concurrent predictions.');
  const [showStartForm, setShowStartForm] = useState(false);

  // restore autosaved draft
  useEffect(() => {
    try {
      const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}');
      if (draft.projectName) setProjectName(draft.projectName);
      if (draft.initialIdea) setInitialIdea(draft.initialIdea);
    } catch {}
  }, []);

  // autosave on change
  useEffect(() => {
    const draft = { projectName, initialIdea };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [projectName, initialIdea]);

  const nameOk = wordCount(projectName) >= MIN_NAME_WORDS;
  const ideaOk = wordCount(initialIdea) >= MIN_IDEA_WORDS;
  const isValid = nameOk && ideaOk;

  const handleStartDesign = async () => {
    if (!isValid || isStarting) return;
    setIsStarting(true);
    try {
      const session = await createSession(user?.userId, projectName.trim(), initialIdea.trim());
      localStorage.removeItem(DRAFT_KEY);
      navigate(`/project/${session.sessionId}/gather`);
    } catch (error) {
      console.error('Failed to start session:', error);
      // consider a toast/inline error message here
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <Layout>
      <div className="container service-page">
        <div className="page-heading">
          <h1>From Vision to Architecture</h1>
          <p>
            Transform your ideas into comprehensive architecture documentation through intelligent questioning.
            No diagram selection required (unless you want to) — we'll generate the right artifacts based on your requirements.
          </p>
        </div>

        <div className="service-content">
          <div className="overview-section">
            <h2>Requirements-First Architecture Design</h2>
            <p>
              Skip the guesswork. Our AI conducts intelligent interviews to understand your business context,
              technical constraints, and quality requirements—then automatically generates the right architecture artifacts.
            </p>
          </div>

          <div className="workflow-preview">
            <h2>How It Works</h2>
            <div className="workflow-steps">
              <div className="workflow-step">
                <div className="step-icon">💬</div>
                <h3>1. Describe Your Vision</h3>
                <p>Tell us what you want to build in plain language. "I need an e-commerce platform that handles 10K orders/day."</p>
              </div>
              <div className="workflow-step">
                <div className="step-icon">🧠</div>
                <h3>2. Guided Interview</h3>
                <p>Answer ~12 smart questions about business context, security, performance, and operations.</p>
              </div>
              <div className="workflow-step">
                <div className="step-icon">📋</div>
                <h3>3. Requirements Review</h3>
                <p>Review and confirm the structured requirements document we generate from your answers.</p>
              </div>
              <div className="workflow-step">
                <div className="step-icon">🎯</div>
                <h3>4. Auto-Generated Artifacts</h3>
                <p>Get the right diagrams and documents: C4 models, HLD outlines, ERDs, and more—based on your needs.</p>
              </div>
            </div>
          </div>

          <div className="progressive-artifacts">
            <h2>Progressive Artifact Generation</h2>
            <div className="artifact-phases">
              <div className="phase-item">
                <h3>🏗️ Foundation Phase</h3>
                <p><strong>Always Generated:</strong> C4 Context & Container diagrams, HLD/SAD document outlines</p>
                <small>Unlocked after: Business context + basic requirements</small>
              </div>
              <div className="phase-item">
                <h3>🗄️ Data Model Phase</h3>
                <p><strong>When Relevant:</strong> Entity Relationship Diagrams, data flow documentation</p>
                <small>Unlocked after: Core entities and data requirements defined</small>
              </div>
              <div className="phase-item">
                <h3>🔌 API Design Phase</h3>
                <p><strong>When Needed:</strong> Sequence diagrams, OpenAPI specifications</p>
                <small>Unlocked after: Integration points and user journeys specified</small>
              </div>
              <div className="phase-item">
                <h3>☁️ Deployment Phase</h3>
                <p><strong>When Ready:</strong> Infrastructure diagrams, deployment templates</p>
                <small>Unlocked after: Hosting and operations strategy defined</small>
              </div>
            </div>
          </div>

          <div className="example-section">
            <h2>Example: E-commerce Platform</h2>
            <div className="example-flow">
              <div className="example-input">
                <h4>Your Input:</h4>
                <p>"I need an e-commerce platform for selling physical products with 1K-10K daily orders, PCI compliance, and AWS deployment."</p>
              </div>
              <div className="example-questions">
                <h4>Smart Questions Asked:</h4>
                <ul>
                  <li>What types of products? (Physical goods, digital, services...)</li>
                  <li>Peak order volumes and target regions?</li>
                  <li>Identity provider preference? (Cognito, Auth0...)</li>
                  <li>Core entities? (Product, Order, Customer, Inventory...)</li>
                  <li>Payment gateways? (Stripe, PayPal, Adyen...)</li>
                  <li>Deployment strategy? (Serverless, containers...)</li>
                </ul>
              </div>
              <div className="example-output">
                <h4>Generated Artifacts:</h4>
                <ul>
                  <li>✅ C4 Context & Container diagrams</li>
                  <li>✅ HLD document outline</li>
                  <li>✅ Entity Relationship Diagram</li>
                  <li>✅ API sequence diagrams</li>
                  <li>✅ AWS deployment architecture</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="cta-section">
            <h2>Start Your Architecture Design</h2>
            <p>Transform your vision into comprehensive architecture documentation in minutes, not weeks.</p>

            {!showStartForm ? (
              <button
                className="cta-button"
                onClick={() => setShowStartForm(true)}
              >
                Start Architecture Design
              </button>
            ) : (
              <div className="start-form">
                <div className="form-group">
                  <label htmlFor="projectName">Project Name:</label>
                  <input
                    type="text"
                    id="projectName"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="ProphetPlay.ai"
                    className={`form-input ${!nameOk ? 'invalid' : ''}`}
                    aria-invalid={!nameOk}
                  />
                  <small>{wordCount(projectName)} words • need ≥ {MIN_NAME_WORDS}</small>
                </div>

                <div className="form-group">
                  <label htmlFor="initialIdea">Describe Your Vision:</label>
                  <textarea
                    id="initialIdea"
                    value={initialIdea}
                    onChange={(e) => setInitialIdea(e.target.value)}
                    placeholder="Describe your prediction platform vision..."
                    className={`form-textarea ${!ideaOk ? 'invalid' : ''}`}
                    rows={4}
                    aria-invalid={!ideaOk}
                  />
                  <small>{wordCount(initialIdea)} words • aim for ≥ {MIN_IDEA_WORDS}</small>
                </div>

                <div className="form-actions">
                  <button
                    className="cta-button"
                    onClick={handleStartDesign}
                    disabled={!isValid || isStarting}
                    aria-busy={isStarting}
                  >
                    {isStarting ? 'Starting…' : 'Begin Interview'}
                  </button>
                  <button
                    className="secondary-button"
                    onClick={() => setShowStartForm(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ArchitectureDesign;